import axios from 'axios';
import { History } from 'history';

const api_url = process.env.REACT_APP_API_URL;

export const getRecruiterList = async (query?: string) => {
  return await axios.get(`${api_url}recruiters/${query ? query : ''}`);
};

export const getRecrutersSelectedForJob = async ({
  query,
  job_id,
}: {
  query?: string;
  job_id?: string | null;
}) => {
  return await axios.get(
    `${api_url}recruiters/selected/?job_tier=${query ? query : ''
    }&selected=true${job_id ? `&job_id=${job_id}` : ''}}`,
  );
};

export const getRecrutersForRecruiterManagement = async (
  showMarketplaceRecruiters: boolean,
  query?: string,
) => {
  return await axios.get(
    `${api_url}recruiters/recruiter-management/?own_recruiter=${showMarketplaceRecruiters}&${query ? query : ''
    }`,
  );
};

export const getPreviouslySelectedRecruiters = async (query?: string) => {
  return await axios.get(
    `${api_url}recruiters/previously-selected/?job_tier=${query ? query : ''}`,
  );
};

export const getRecruiterCompanyAgreements = async () => {
  return await axios.get(`${api_url}recruiters-agreement/`);
};

export const getRecruiterCompanyAgreement = async (
  recruiterCompanyAgreementId: string,
) => {
  return await axios.get(
    `${api_url}recruiters-agreement/${recruiterCompanyAgreementId}/`,
  );
};

export const getRecruiterCompanyAgreementByCompany = async (
  companyId: string,
) => {
  return await axios.get(
    `${api_url}recruiters-agreement/`, {
    params: {
      company: companyId,
    }
  }
  );
};

export const patchRecruiterCompanyAgreement = async (
  id: string,
  payload: any,
) => await axios.patch(`${api_url}recruiters-agreement/${id}/`, payload);

export const deleteRecruiterCompanyAgreement = async ({
  recruiter_id,
  client_company_id,
}: {
  recruiter_id: string;
  client_company_id: string;
}) => {
  return await axios.delete(`${api_url}recruiters-agreement/delete/`, {
    params: {
      recruiter_id,
      client_company_id,
    },
  });
};

export const sendInviteToRecruiters = async ({
  company_name,
  selected_recruiters = [],
  loaded_recruiters = [],
  job_id,
}: {
  company_name: string;
  selected_recruiters?: string[];
  loaded_recruiters?: string[];
  job_id: string;
}) => {
  return await axios.post(`${api_url}recruiters/invite-list/`, {
    company_name,
    selected_recruiters,
    loaded_recruiters,
    job_id,
  });
};

export const inviteAllRecruitersToJob = async ({
  company_name,
  job_id,
  unselected_recruiters,
}: {
  company_name: string;
  job_id: string;
  unselected_recruiters: string[];
}) => {
  return await axios.post(`${api_url}recruiters/invite-all/`, {
    company_name,
    job_id,
    unselected_recruiters,
  });
};

export const inviteOwnSupplier = async (reqBody: FormData) => {
  return await axios.post(`${api_url}recruiters/invite-own/`, reqBody);
};

export const getRecruiter = async (id: string, history?: History) => {
  return await axios
    .get(`${api_url}recruiters/${id}/`)
    .then((res) => res)
    .catch((e) => {
      if (e?.response?.data?.error_code === 'resource_not_found' && history)
        history.push('/not-found');
      return e.response;
    });
};

export const editRecruiter = async (id: string, info: any) => {
  return await axios.put(`${api_url}recruiters/${id}/`, info);
};

export const sendRecruiterInvitation = async (info: any) => {
  const company =
    info.newCompany &&
    (await createRecruiterCompany({ name: info.newCompany }));
  if (info.newCompany) {
    info.company = company.data.id;
  }
  delete info.newCompany;
  const invite = await axios.post(`${api_url}recruiters/invite/`, info);
  return invite;
};

export const getRecruiterInfo = async () => {
  return await axios.get(`${api_url}recruiters/me/`);
};

export const updateRecruiter = async (info: any) => {
  return await axios.patch(`${api_url}recruiters/me/`, info);
};

export const acceptRecruiterTerms = async (agreementId: string, info: any) => {
  return await axios.post(`${api_url}recruiters-agreement/${agreementId}/accept/`, info);
};

export const declineRecruiterTerms = async (agreementId: string, info: any) => {
  return await axios.post(`${api_url}recruiters-agreement/${agreementId}/decline/`, info);
};

export const createRecruiterCompany = async (info: any) => {
  return await axios.post(`${api_url}recruiter-companies/`, info);
};

export const getRecruitersCompanyList = async () => {
  return await axios.get(`${api_url}recruiter-companies/`);
};

export const updateRecruiterCompanyLanguage = async (companyId: string, language: string) => {
  return await axios.patch(`${process.env.REACT_APP_API_URL}recruiter-companies/${companyId}/`, { default_language: language });
};

export const updateRecruiterCompany = async (companyId: string, data: any) => {
  return await axios.patch(`${api_url}recruiter-companies/${companyId}/`, data);
};

export const updateCompanyVirtualStatus = async (companyId: string, isVirtual: boolean, name?: string) => {
  const data: { is_virtual: boolean, name?: string } = { is_virtual: isVirtual };
  if (name) {
    data.name = name;
  }
  return await axios.patch(`${api_url}recruiter-companies/${companyId}/`, data);
};

export const createRecruiterProfile = async (info: {
  email: string;
  first_name: string;
  last_name?: string;
  city: string;
  phone?: string;
  is_sourcer?: boolean;
}) => {
  return await axios.post(`${api_url}recruiter-profiles/create/`, info);
};

export const getCompanyRecruiters = async (
  companyId: string,
  page: number = 1,
  pageSize: number = 10
) => {
  return await axios.get(`${api_url}recruiter-profiles/company-users/`, {
    params: {
      page,
      page_size: pageSize
    }
  });
};

export const toggleRecruiterActiveStatus = async (profileId: string) => {
  return await axios.post(`${api_url}recruiter-profiles/${profileId}/toggle-active/`);
};

export const setRecruiterExtensionPermission = async (jobId: string, userId: string, grant: boolean) => {
  return await axios.post(`${process.env.REACT_APP_API_URL}jobs/${jobId}/set-recruiter-extension-permission/`, {
    user_id: userId,
    grant,
  });
};

export const getJobAssignmentsOverview = async (params: {
  search?: string;
  status?: string;
  recruiter_company?: string;
  job_title?: string;
  sort?: string;
  page?: number;
} = {}) => {
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.recruiter_company) queryParams.append('recruiter_company', params.recruiter_company);
  if (params.job_title) queryParams.append('job_title', params.job_title);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.page) queryParams.append('page', params.page.toString());

  const url = `${process.env.REACT_APP_API_URL}recruiters/job-assignments/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return await axios.get(url);
};

export const getJobAssignmentsFilterOptions = async () => {
  const url = `${process.env.REACT_APP_API_URL}recruiters/job-assignments-filters/`;
  return await axios.get(url);
};
